import * as Sentry from '@sentry/react';
import type { BoardTelemetryEvent } from '../../ui/components/board';
import { ALL_STAGES } from './constants';
import type { ProductItem, ProductItemStage } from './types';

export type ProductBoardMutationStatus = 'idle' | 'pending' | 'success' | 'error';

export interface ProductBoardTelemetrySnapshot {
  route: '/boards/products';
  includeDeprecated: boolean;
  searchPresent: boolean;
  searchLength: number;
  priorityIds: string[];
  customerId: string | null;
  activeItemId: string | null;
  drawerOpen: boolean;
  mutationStatus: ProductBoardMutationStatus;
  mutationPending: boolean;
  isLoading: boolean;
  visibleStageCount: number;
  totalItemCount: number;
  columnCounts: Record<ProductItemStage, number>;
}

export function buildProductBoardTelemetrySnapshot(params: {
  includeDeprecated: boolean;
  searchText: string;
  priorityIds: readonly string[];
  customerId: string | null;
  activeItemId: string | null;
  mutationStatus: ProductBoardMutationStatus;
  mutationPending: boolean;
  isLoading: boolean;
  visibleStages: readonly ProductItemStage[];
  itemsByColumn: Record<ProductItemStage, ProductItem[]>;
}): ProductBoardTelemetrySnapshot {
  const columnCounts = {} as Record<ProductItemStage, number>;
  let totalItemCount = 0;
  for (const stage of ALL_STAGES) {
    const count = params.itemsByColumn[stage]?.length ?? 0;
    columnCounts[stage] = count;
    totalItemCount += count;
  }
  const search = params.searchText.trim();

  return {
    route: '/boards/products',
    includeDeprecated: params.includeDeprecated,
    searchPresent: search.length > 0,
    searchLength: search.length,
    priorityIds: [...params.priorityIds],
    customerId: params.customerId,
    activeItemId: params.activeItemId,
    drawerOpen: params.activeItemId !== null,
    mutationStatus: params.mutationStatus,
    mutationPending: params.mutationPending,
    isLoading: params.isLoading,
    visibleStageCount: params.visibleStages.length,
    totalItemCount,
    columnCounts,
  };
}

export function buildProductBoardRenderBreadcrumbData(
  snapshot: ProductBoardTelemetrySnapshot
): Record<string, string | number | boolean | null> {
  return {
    route: snapshot.route,
    include_deprecated: snapshot.includeDeprecated,
    search_present: snapshot.searchPresent,
    search_length: snapshot.searchLength,
    priority_count: snapshot.priorityIds.length,
    priority_ids: snapshot.priorityIds.length > 0 ? snapshot.priorityIds.join(',') : null,
    customer_filter_id: snapshot.customerId,
    active_item_id: snapshot.activeItemId,
    drawer_open: snapshot.drawerOpen,
    mutation_status: snapshot.mutationStatus,
    mutation_pending: snapshot.mutationPending,
    is_loading: snapshot.isLoading,
    visible_stage_count: snapshot.visibleStageCount,
    total_item_count: snapshot.totalItemCount,
    ...flattenColumnCounts('column_count', snapshot.columnCounts),
  };
}

export function buildProductBoardEventBreadcrumbData(
  snapshot: ProductBoardTelemetrySnapshot,
  event: BoardTelemetryEvent<ProductItemStage, ProductItem>
): Record<string, string | number | boolean | null> {
  const item = event.item;
  const targetColumnId = event.toColumnId ?? event.overColumnId;
  const sourceColumnId = event.sourceColumnId;
  const boardCounts = event.snapshotColumnItemCounts ?? event.columnItemCounts;

  return {
    ...baseBreadcrumbData(snapshot),
    phase: event.phase,
    drag_active: event.phase === 'drag_start' || event.phase === 'drag_over',
    item_id: event.itemId,
    item_stage: item?.stage ?? null,
    item_priority: item?.priority ?? null,
    item_flag_driven: item?.flagDriven ?? null,
    item_flag_count: item?.flagCount ?? null,
    source_column_id: sourceColumnId,
    over_column_id: event.overColumnId,
    target_column_id: targetColumnId,
    same_column:
      sourceColumnId !== null && targetColumnId !== null ? sourceColumnId === targetColumnId : null,
    before_item_id: event.beforeItemId,
    after_item_id: event.afterItemId,
    validation_result: event.validationResult,
    drop_accepted: event.dropAccepted,
    source_column_count: sourceColumnId ? snapshot.columnCounts[sourceColumnId] : null,
    target_column_count: targetColumnId ? snapshot.columnCounts[targetColumnId] : null,
    board_source_column_count: sourceColumnId ? boardCounts[sourceColumnId] : null,
    board_target_column_count: targetColumnId ? boardCounts[targetColumnId] : null,
  };
}

export function recordProductBoardRenderTelemetry(snapshot: ProductBoardTelemetrySnapshot): void {
  const data = buildProductBoardRenderBreadcrumbData(snapshot);
  Sentry.setTag('feature', 'product-board');
  Sentry.setTag('product_board.route', 'products');
  Sentry.setTag('product_board.drawer_open', String(snapshot.drawerOpen));
  Sentry.setTag('product_board.mutation_status', snapshot.mutationStatus);
  Sentry.setContext('product_board', data);
  Sentry.addBreadcrumb({
    category: 'product_board.render',
    level: 'info',
    message: 'product board render snapshot',
    data,
  });
}

export function recordProductBoardEventTelemetry(
  snapshot: ProductBoardTelemetrySnapshot,
  event: BoardTelemetryEvent<ProductItemStage, ProductItem>
): void {
  const data = buildProductBoardEventBreadcrumbData(snapshot, event);
  Sentry.setTag('feature', 'product-board');
  Sentry.setTag('product_board.route', 'products');
  Sentry.setTag('product_board.last_event', event.phase);
  Sentry.setTag('product_board.mutation_status', snapshot.mutationStatus);
  if (event.itemId) {
    Sentry.setTag('product_board.item_id', event.itemId);
  }
  Sentry.setContext('product_board_last_event', data);
  Sentry.addBreadcrumb({
    category: 'product_board.board',
    level: event.phase === 'drop_rejected' ? 'warning' : 'info',
    message: event.phase,
    data,
  });
}

function baseBreadcrumbData(
  snapshot: ProductBoardTelemetrySnapshot
): Record<string, string | number | boolean | null> {
  return {
    route: snapshot.route,
    include_deprecated: snapshot.includeDeprecated,
    search_present: snapshot.searchPresent,
    search_length: snapshot.searchLength,
    priority_count: snapshot.priorityIds.length,
    priority_ids: snapshot.priorityIds.length > 0 ? snapshot.priorityIds.join(',') : null,
    customer_filter_id: snapshot.customerId,
    active_item_id: snapshot.activeItemId,
    drawer_open: snapshot.drawerOpen,
    mutation_status: snapshot.mutationStatus,
    mutation_pending: snapshot.mutationPending,
    total_item_count: snapshot.totalItemCount,
  };
}

function flattenColumnCounts(
  prefix: string,
  counts: Record<ProductItemStage, number>
): Record<string, number> {
  const entries = ALL_STAGES.map((stage) => [`${prefix}_${stage}`, counts[stage] ?? 0] as const);
  return Object.fromEntries(entries);
}
